import PropTypes from "prop-types";

import AudioKey from "../../lib/AudioKey";
import {
	MODE_MAJOR,
	MODE_MINOR,
	getRelativeKey,
	getRichPitchName,
	modeHasMinorQuality,
	pitchNames
} from "../../lib/audioTools";

import "./PKey.scss";

function PKey({ audioKey }) {
	if (!(audioKey instanceof AudioKey)) {
		return null;
	}

	const relativeKey = getRelativeKey(audioKey);
	let majorKey = null;
	let minorKey = null;

	if (audioKey.mode === MODE_MAJOR) {
		majorKey = audioKey;
		minorKey = relativeKey;
	} else if (audioKey.mode === MODE_MINOR) {
		majorKey = relativeKey;
		minorKey = audioKey;
	}

	// "Key in mode" is a bad shorthand that just means the key is non-major-minor
	const keyInMode = audioKey.mode !== MODE_MAJOR && audioKey.mode !== MODE_MINOR;
	const hasMinorQuality = keyInMode && modeHasMinorQuality(audioKey.mode);

	return (
		<div
			className="pkey-canvas"
			aria-label="Visual representation of pitches in a musical key on a piano"
		>
			<div className="pkey-canvas__inner">
				{audioKey.pitches.map((pitch, i) => {
					const pitchName = audioKey.pitchNames[i];
					const neutralPitchName = pitchNames[pitch][0];
					const isFlat = neutralPitchName.length > 1;
					let isMajor = false;
					let isMinor = false;
					let tonicDescriptor = "";

					if (!keyInMode) {
						isMajor = majorKey instanceof AudioKey && majorKey.pitch === pitch;
						isMinor = minorKey instanceof AudioKey && minorKey.pitch === pitch;

						if (isMajor) {
							tonicDescriptor = "Major tonic";
						} else if (isMinor) {
							tonicDescriptor = "Minor tonic";
						}
					} else if (pitch === audioKey.pitch) {
						isMajor = !hasMinorQuality;
						isMinor = hasMinorQuality;
						tonicDescriptor = hasMinorQuality
							? "Tonic with a minor quality"
							: "Tonic with a major quality";
					}

					const classList = [
						"pkey",
						`pkey--${isFlat ? "flat" : "natural"}`,
						`pkey--${pitchName.toLowerCase()}`
					];

					if (isMajor) {
						classList.push("pkey--major");
					} else if (isMinor) {
						classList.push("pkey--minor");
					}

					const title = `${getRichPitchName(pitchName)}${tonicDescriptor ? ` (${tonicDescriptor})` : ""}`;

					return (
						<div
							className={classList.join(" ")}
							title={title}
							key={pitchName}
						>
							<span />
						</div>
					);
				})}
			</div>
		</div>
	);
}

PKey.propTypes = {
	audioKey: PropTypes.shape({
		mode: PropTypes.number,
		pitch: PropTypes.number,
		pitches: PropTypes.arrayOf(PropTypes.number),
		pitchNames: PropTypes.arrayOf(PropTypes.string),
		richName: PropTypes.func
	}).isRequired
};

export default PKey;
